<!--
  The two-factor lockout screen (#282). Deliberately offers no navigation: the guard would bounce every link
  straight back here, so a link is a promise the app cannot keep. The two real exits are a completed enrolment
  (handled by the panel below) and signing out.

  `max-w-3xl mx-auto` + `px-4 sm:px-6` is the same frame the profile card uses, so the panel it hosts looks
  identical in both places — the point of reusing the component rather than copying it.
-->
<div class="mx-auto max-w-3xl px-4 py-8 sm:px-6">
  <div class="overflow-hidden bg-white shadow sm:rounded-lg">
    <div class="px-4 py-6 sm:px-6">
      <h1 class="text-lg font-semibold text-gray-900">Zwei-Faktor-Authentifizierung erforderlich</h1>
      <!--
        Two sentences for two genuinely different situations (#279), not a cosmetic variant. A restricted
        session really can reach nothing else; a session refused at CONTEXT ENTRY can — it simply cannot enter
        that one workspace. Telling the second one it is locked out of the app would be false, and it is exactly
        the sentence that would stop somebody from going to the company they can work in today.
      -->
      @if (refusedAtContextEntry) {
        <p class="mt-2 text-sm text-gray-700">
          Der gewählte Arbeitsbereich verlangt für Ihr Konto einen zweiten Faktor. Richten Sie ihn hier ein, um
          ihn zu betreten — andere Arbeitsbereiche bleiben offen.
        </p>
      } @else {
        <p class="mt-2 text-sm text-gray-700">
          Ihr Unternehmen verlangt für Ihr Konto einen zweiten Faktor. Bis dieser eingerichtet ist, können Sie
          nur diese Seite aufrufen.
        </p>
      }
    </div>

    <div class="border-t border-gray-300 px-4 py-6 sm:px-6">
      <app-two-factor-panel (enrolled)="onEnrolled()"/>
    </div>

    <div class="border-t border-gray-300 px-4 py-4 sm:px-6">
      @if (finishing()) {
        <p class="text-sm text-gray-600">Ihre Sitzung wird aktualisiert …</p>
      }

      @if (errorMessage(); as message) {
        <div class="space-y-2">
          <p role="alert" class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ message }}</p>
          <!-- The enrolment itself already succeeded; only the session refresh failed, so the retry repeats
               just that rather than sending anybody back through the QR code a second time. -->
          <button type="button" (click)="retry()" [disabled]="finishing()"
                  class="inline-flex items-center justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 disabled:pointer-events-none disabled:bg-gray-300 disabled:text-gray-600">
            Erneut versuchen
          </button>
        </div>
      }

      @if (stillRestricted()) {
        <p role="alert" class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Ihr Zugang ist weiterhin eingeschränkt. Vermutlich verlangt Ihr Unternehmen die Anmeldung über einen
          externen Anbieter — bitte wenden Sie sich an Ihre Administration.
        </p>
      }

      <div class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-xs text-gray-500">
          Fragen? Ihre Administration kann Ihnen sagen, welche Vorgabe für Ihr Unternehmen gilt.
        </p>
        <!--
          Stacked on phones, side by side from `sm` — the same reflow the rest of this card uses, so two
          buttons do not squeeze the sign-out target below a comfortable tap size on a narrow viewport.

          A `button` rather than an `<a routerLink>`, deliberately: the file header's "offers no navigation"
          rule still holds for a RESTRICTED session, and this control only exists for the context-entry one
          (see `toChooser`). Rendering it as a link would put an anchor into the restricted screen's DOM the
          moment somebody made the condition slightly wider.
        -->
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          @if (refusedAtContextEntry) {
            <button type="button" (click)="toChooser()"
                    class="inline-flex items-center justify-center rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700">
              Anderen Bereich wählen
            </button>
          }
          <button type="button" (click)="logout()"
                  class="inline-flex items-center justify-center rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Abmelden
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
